import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface UseSearchParamsSyncProps {
  currentFilters: string[];
  updateFilters: (
    filtersToUpdate: Array<{ columnId: string; value: string[] }>,
    removedKeys: string[]
  ) => void;
}

/**
 * Custom hook to synchronize search params changes with datatable filter context
 * Maintains URL as source of truth while preventing infinite loops
 * Returns true when initial sync from URL is complete
 */
export function useSearchParamsSync({ currentFilters, updateFilters }: UseSearchParamsSyncProps) {
  const [searchParams] = useSearchParams();

  // Track if initial sync from URL has completed
  const [isInitialized, setIsInitialized] = useState(false);

  // Convert search params to stable string for dependency comparison to avoid infinite loop
  const searchParamsString = useMemo(() => searchParams.toString(), [searchParams]);

  // Get registered keys as a stable string for comparison
  const registeredKeysString = useMemo(() => {
    return currentFilters.sort().join(",");
  }, [currentFilters]);

  // Track previous keys to detect removals
  const prevKeysRef = useRef<string>("");

  // Track stabilization of filters
  const stabilizationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKeysRef = useRef<string>("");

  // Synchronize search params changes with context filters
  useEffect(() => {
    const search = new URLSearchParams(searchParamsString);
    const currentKeys = registeredKeysString.split(",").filter(Boolean);
    const prevKeys = prevKeysRef.current.split(",").filter(Boolean);

    // Skip if no keys are registered yet
    if (currentKeys.length === 0) {
      // Clear any pending stabilization timer
      if (stabilizationTimerRef.current) {
        clearTimeout(stabilizationTimerRef.current);
        stabilizationTimerRef.current = null;
      }
      setIsInitialized(false);
      return;
    }

    // Find removed keys (in previous but not in current)
    const removedKeys = prevKeys.filter(key => !currentKeys.includes(key));

    // Build filter updates for all current keys
    const filtersToUpdate: Array<{ columnId: string; value: string[] }> = [];

    currentKeys.forEach(searchKey => {
      // Get all values for this key (handles both single and multi-value)
      const values = search.getAll(searchKey);

      // Always update the filter with the values (empty array if no values)
      filtersToUpdate.push({
        columnId: searchKey,
        value: values,
      });
    });

    // Check if keys have changed
    const keysChanged = registeredKeysString !== lastKeysRef.current;
    lastKeysRef.current = registeredKeysString;

    // If keys changed, reset the stabilization timer
    if (keysChanged) {
      // Clear existing timer
      if (stabilizationTimerRef.current) {
        clearTimeout(stabilizationTimerRef.current);
      }

      // Set a new timer to mark as initialized after filters stabilize
      // Wait 100ms for any additional filter registrations
      stabilizationTimerRef.current = setTimeout(() => {
        setIsInitialized(true);
        stabilizationTimerRef.current = null;
      }, 100);
    }

    updateFilters(filtersToUpdate, removedKeys);
    // Update the ref for next comparison
    prevKeysRef.current = registeredKeysString;
  }, [searchParamsString, registeredKeysString, updateFilters]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (stabilizationTimerRef.current) {
        clearTimeout(stabilizationTimerRef.current);
      }
    };
  }, []);

  return isInitialized;
}
